import {
	AI_AGENT_KNOWLEDGE_DOCUMENT_MAX_BYTES,
	type AiAgentKnowledgePage,
	type AiAgentKnowledgeSource,
	type AiAgentKnowledgeSourceKind,
	type AiKnowledgeCollection,
} from '@livechat/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import {
	createAiKnowledgeSource,
	deleteAiKnowledgeSource,
	listAiKnowledgeSources,
	refreshAiKnowledgeSource,
	uploadAiKnowledgeDocument,
} from '../api.js';
import { useDateFormat } from '../date-format.js';

const PAGE_SIZE = 10;
const EMPTY_SOURCES: AiAgentKnowledgePage = { items: [], page: 1, pageSize: PAGE_SIZE, total: 0 };

function sourceLabel(source: AiAgentKnowledgeSource): string {
	if (source.kind === 'document') return source.fileName ?? 'Document';
	if (source.kind === 'text') return 'Manual text';
	return source.sourceUrl ?? (source.kind === 'crawl' ? 'Website crawl' : 'Web page');
}

function RefreshIcon(): React.JSX.Element {
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			<path d="M20 6v5h-5" />
			<path d="M19 11a7 7 0 1 0 .2 3.5" />
		</svg>
	);
}

function DeleteIcon(): React.JSX.Element {
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			<path d="M4 7h16" />
			<path d="M9 7V4h6v3" />
			<path d="m7 7 1 13h8l1-13" />
			<path d="M10 11v5M14 11v5" />
		</svg>
	);
}

export function KnowledgeSources({
	collection,
	tenantId,
	active,
	disabled = false,
	dirty,
	onDirtyChange,
	onBusyChange,
}: {
	collection: AiKnowledgeCollection;
	tenantId: string;
	active: boolean;
	disabled?: boolean;
	dirty: boolean;
	onDirtyChange: (dirty: boolean) => void;
	onBusyChange: (busy: boolean) => void;
}) {
	const { formatDate } = useDateFormat();
	const [sources, setSources] = useState<AiAgentKnowledgePage>(EMPTY_SOURCES);
	const [sourcePage, setSourcePage] = useState(1);
	const [sourceKind, setSourceKind] = useState<AiAgentKnowledgeSourceKind>('url');
	const [showAddSource, setShowAddSource] = useState(false);
	const [operationBusy, setBusy] = useState(false);
	const busy = operationBusy || disabled;
	const [sourcesLoading, setSourcesLoading] = useState(true);
	const [sourceError, setSourceError] = useState<string>();
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	const [reload, setReload] = useState(0);
	const mounted = useRef(true);
	const titleInput = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (showAddSource) titleInput.current?.focus();
	}, [showAddSource]);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	useEffect(() => {
		onBusyChange(operationBusy);
	}, [operationBusy, onBusyChange]);

	// Each selected collection is a separate keyed workspace. Also ignore responses from a
	// previous page/poll after navigation or a mutation, so sources never cross scopes.
	useEffect(() => {
		if (!active || busy) return;
		let current = true;
		let timer: number | undefined;
		setSources(EMPTY_SOURCES);
		setSourcesLoading(true);
		setSourceError(undefined);
		async function load(silent = false): Promise<void> {
			try {
				const loaded = await listAiKnowledgeSources(tenantId, collection.id, {
					page: sourcePage,
					pageSize: PAGE_SIZE,
				});
				if (!current) return;
				const lastPage = Math.max(1, Math.ceil(loaded.total / loaded.pageSize));
				if (sourcePage > lastPage) {
					setSourcePage(lastPage);
					return;
				}
				setSources(loaded);
				setSourceError(undefined);
			} catch (caught) {
				if (current && !silent)
					setSourceError(
						caught instanceof Error
							? caught.message
							: 'Could not load knowledge sources',
					);
			} finally {
				if (current) {
					setSourcesLoading(false);
					timer = window.setTimeout(() => void load(true), 5_000);
				}
			}
		}
		void load();
		return () => {
			current = false;
			window.clearTimeout(timer);
		};
	}, [active, busy, collection.id, reload, sourcePage, tenantId]);

	function cancelSource(): void {
		if (dirty && !window.confirm('Discard this unsaved source?')) return;
		setShowAddSource(false);
		setSourceKind('url');
		onDirtyChange(false);
		setError(undefined);
	}
	async function addSource(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!collection.id || busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);
		try {
			const title = String(data.get('title'));
			if (sourceKind === 'document') {
				const file = data.get('document');
				if (!(file instanceof File) || !file.size) throw new Error('Choose a document');
				if (file.size > AI_AGENT_KNOWLEDGE_DOCUMENT_MAX_BYTES) {
					throw new Error('Knowledge documents must be 10 MB or smaller');
				}
				await uploadAiKnowledgeDocument(tenantId, collection.id, { file, title });
			} else if (sourceKind === 'text') {
				await createAiKnowledgeSource(tenantId, collection.id, {
					kind: sourceKind,
					text: String(data.get('text')),
					title,
				});
			} else {
				await createAiKnowledgeSource(tenantId, collection.id, {
					kind: sourceKind,
					title,
					url: String(data.get('url')),
				});
			}
			if (!mounted.current) return;
			form.reset();
			onDirtyChange(false);
			setShowAddSource(false);
			setSourceKind('url');
			setSourcePage(1);
			setReload((value) => value + 1);
			setMessage('Knowledge source added for processing.');
		} catch (caught) {
			if (mounted.current)
				setError(
					caught instanceof Error ? caught.message : 'Could not add knowledge source',
				);
		} finally {
			if (mounted.current) setBusy(false);
		}
	}

	async function refreshSource(source: AiAgentKnowledgeSource): Promise<void> {
		if (!collection.id || busy || !['url', 'crawl'].includes(source.kind)) return;
		setBusy(true);
		setError(undefined);
		try {
			const refreshed = await refreshAiKnowledgeSource(tenantId, collection.id, source.id);
			if (!mounted.current) return;
			setSources((current) => ({
				...current,
				items: current.items.map((item) => (item.id === refreshed.id ? refreshed : item)),
			}));
		} catch (caught) {
			if (mounted.current)
				setError(
					caught instanceof Error ? caught.message : 'Could not refresh knowledge source',
				);
		} finally {
			if (mounted.current) setBusy(false);
		}
	}

	async function removeSource(source: AiAgentKnowledgeSource): Promise<void> {
		if (
			!collection.id ||
			busy ||
			!window.confirm(`Delete "${source.title}" from this collection?`)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		try {
			await deleteAiKnowledgeSource(tenantId, collection.id, source.id);
			if (!mounted.current) return;
			const targetPage =
				sources.items.length === 1 && sourcePage > 1 ? sourcePage - 1 : sourcePage;
			if (targetPage !== sourcePage) setSourcePage(targetPage);
			else setReload((value) => value + 1);
		} catch (caught) {
			if (mounted.current)
				setError(
					caught instanceof Error ? caught.message : 'Could not delete knowledge source',
				);
		} finally {
			if (mounted.current) setBusy(false);
		}
	}

	const pageCount = Math.max(1, Math.ceil(sources.total / sources.pageSize));
	return (
		<div className="knowledge-source-workspace">
			{error && (
				<div className="alert error" role="alert">
					{error}
				</div>
			)}
			{message && (
				<div className="alert success" role="status">
					{message}
				</div>
			)}

			<section
				className="settings-card ai-agent-knowledge-table-panel"
				aria-labelledby="collection-sources-heading"
			>
				<div className="knowledge-toolbar">
					<div className="integration-panel-heading">
						<h4 className="panel-title" id="collection-sources-heading">
							Sources
						</h4>
						<p>
							Only sources in this collection are shown. Ready means searchable, not
							reviewed for accuracy.
						</p>
					</div>
					<button
						className="primary-button"
						type="button"
						disabled={busy || showAddSource}
						onClick={() => setShowAddSource(true)}
					>
						Add source
					</button>
				</div>
				{showAddSource && (
					<section
						className="ai-agent-knowledge-panel knowledge-inline-source-form"
						aria-label={`Add source to ${collection.name}`}
					>
						<div className="integration-panel-heading">
							<h4 className="panel-title">Add source to {collection.name}</h4>
							<p>
								Add a web page, website crawl, text or document to {collection.name}
								.
							</p>
						</div>
						<form
							onChange={() => onDirtyChange(true)}
							className="ai-agent-knowledge-form"
							onSubmit={(event) => void addSource(event)}
						>
							<fieldset className="knowledge-form-fields" disabled={busy}>
								<div className="settings-grid">
									<label>
										<span>Source type</span>
										<select
											onChange={(event) =>
												setSourceKind(
													event.currentTarget
														.value as AiAgentKnowledgeSourceKind,
												)
											}
											value={sourceKind}
										>
											<option value="url">Web page</option>
											<option value="crawl">Website crawl</option>
											<option value="text">Text</option>
											<option value="document">Document upload</option>
										</select>
									</label>
									<label>
										<span>Title</span>
										<input
											ref={titleInput}
											maxLength={160}
											name="title"
											required
										/>
									</label>
								</div>
								{sourceKind === 'text' ? (
									<label>
										<span>Knowledge text</span>
										<textarea
											maxLength={500_000}
											name="text"
											required
											rows={8}
										/>
									</label>
								) : sourceKind === 'url' || sourceKind === 'crawl' ? (
									<label>
										<span>
											{sourceKind === 'crawl'
												? 'Starting public URL'
												: 'Public URL'}
										</span>
										<input
											maxLength={2_048}
											name="url"
											placeholder="https://example.com/help/delivery"
											required
											type="url"
										/>
										{sourceKind === 'crawl' && (
											<small>
												Crawls up to 200 linked public pages in the same
												website section.
											</small>
										)}
									</label>
								) : (
									<label>
										<span>Document</span>
										<input
											accept=".txt,.pdf,.doc,.docx,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
											name="document"
											required
											type="file"
										/>
										<small>TXT, PDF, DOC or DOCX up to 10 MB.</small>
									</label>
								)}
								<div className="knowledge-toolbar-actions">
									<button className="primary-button" disabled={busy}>
										{busy ? 'Adding.' : 'Add source'}
									</button>
									<button
										className="secondary-button"
										type="button"
										disabled={busy}
										onClick={cancelSource}
									>
										Cancel
									</button>
								</div>
							</fieldset>
						</form>
					</section>
				)}
				<div className="knowledge-table-card paginated-table">
					<div className="knowledge-table-shell paginated-table-scroll">
						<table
							className="knowledge-table"
							aria-label={`Sources in ${collection.name}`}
							aria-busy={sourcesLoading}
						>
							<thead>
								<tr>
									<th>Title</th>
									<th>Source</th>
									<th>Date added</th>
									<th>URLs</th>
									<th>Chunks</th>
									<th>Status</th>
									<th aria-label="Actions" />
								</tr>
							</thead>
							<tbody>
								{sourcesLoading ? (
									<tr>
										<td className="knowledge-table-empty" colSpan={7}>
											Loading sources.
										</td>
									</tr>
								) : sourceError ? (
									<tr>
										<td className="knowledge-table-empty" colSpan={7}>
											Sources could not be loaded.{' '}
											<button
												className="secondary-button"
												type="button"
												onClick={() => setReload((value) => value + 1)}
											>
												Retry
											</button>
										</td>
									</tr>
								) : sources.items.length ? (
									sources.items.map((source) => {
										const processing = ['pending', 'processing'].includes(
											source.status,
										);
										const webSource = ['url', 'crawl'].includes(source.kind);
										return (
											<tr key={source.id}>
												<td>
													<strong>{source.title}</strong>
												</td>
												<td>
													{webSource && source.sourceUrl ? (
														<a
															href={source.sourceUrl}
															rel="noreferrer"
															target="_blank"
														>
															{sourceLabel(source)}
														</a>
													) : (
														sourceLabel(source)
													)}
												</td>
												<td>{formatDate(source.createdAt)}</td>
												<td>
													{webSource && !processing
														? source.urlCount
														: '-'}
												</td>
												<td>{source.chunkCount}</td>
												<td>
													<span
														className={`knowledge-table-status knowledge-${source.status}`}
													>
														{processing && (
															<span
																aria-hidden="true"
																className="knowledge-spinner"
															/>
														)}
														{source.status.charAt(0).toUpperCase() +
															source.status.slice(1)}
													</span>
													{source.errorMessage && (
														<small className="knowledge-row-error">
															{source.errorMessage}
														</small>
													)}
												</td>
												<td>
													<div className="knowledge-row-actions">
														{webSource && (
															<button
																aria-label={`Recrawl ${source.title}`}
																className="knowledge-icon-button"
																disabled={busy || processing}
																onClick={() =>
																	void refreshSource(source)
																}
																title="Recrawl web source"
																type="button"
															>
																<RefreshIcon />
															</button>
														)}
														<button
															aria-label={`Delete ${source.title}`}
															className="knowledge-icon-button knowledge-delete-button"
															disabled={busy}
															onClick={() =>
																void removeSource(source)
															}
															title="Delete knowledge"
															type="button"
														>
															<DeleteIcon />
														</button>
													</div>
												</td>
											</tr>
										);
									})
								) : (
									<tr>
										<td className="knowledge-table-empty" colSpan={7}>
											No sources in {collection.name} yet. Use Add source to
											add your first web page, text or document.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					{sources.total > sources.pageSize && (
						<div className="knowledge-pagination">
							<button
								className="secondary-button"
								disabled={busy || sourcesLoading || sourcePage <= 1}
								onClick={() => setSourcePage((current) => current - 1)}
								type="button"
							>
								Previous
							</button>
							<span>
								Page {sourcePage} of {pageCount}
							</span>
							<button
								className="secondary-button"
								disabled={busy || sourcesLoading || sourcePage >= pageCount}
								onClick={() => setSourcePage((current) => current + 1)}
								type="button"
							>
								Next
							</button>
						</div>
					)}
				</div>
			</section>
		</div>
	);
}
